import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, Home, RequireAdmin, RequireAuth, RequireOnboarded } from './auth.jsx';
import AdminLayout from './components/AdminLayout.jsx';
import Layout from './components/Layout.jsx';
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import VerifyEmail from './pages/VerifyEmail.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import AdminUsers from './pages/admin/AdminUsers.jsx';
import AdminHoneypotDomains from './pages/admin/AdminHoneypotDomains.jsx';
import AdminUser from './pages/admin/AdminUser.jsx';
import Events from './pages/Events.jsx';
import Account from './pages/Account.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Inbox from './pages/inbox/Inbox.jsx';
import InboxMessage from './pages/inbox/InboxMessage.jsx';
import InboxHeaders from './pages/inbox/InboxHeaders.jsx';
import InboxView from './pages/inbox/InboxView.jsx';
import InboxAdd from './pages/inbox/InboxAdd.jsx';
import Lookalikes from './pages/lookalikes/Lookalikes.jsx';
import LookalikeDetail from './pages/lookalikes/LookalikeDetail.jsx';
import EmailSecurity from './pages/company/EmailSecurity.jsx';
import Plan from './pages/home/Plan.jsx';
import HelpIndex from './pages/help/HelpIndex.jsx';
import HelpArticle from './pages/help/HelpArticle.jsx';
import Contact from './pages/Contact.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import Outbox from './pages/outbox/Outbox.jsx';
import OutboxMessage from './pages/outbox/OutboxMessage.jsx';
import EmailServices from './pages/company/EmailServices.jsx';
import Onboarding from './pages/Onboarding.jsx';
import Setup from './pages/Setup.jsx';
import Company from './pages/company/Company.jsx';
import Brand from './pages/company/Brand.jsx';
import DomainVerify from './pages/company/DomainVerify.jsx';
import Website from './pages/company/Website.jsx';
import ApproveDomain from './pages/ApproveDomain.jsx';
import AcceptInvite from './pages/AcceptInvite.jsx';
import Team from './pages/company/Team.jsx';
import ConfirmSender from './pages/ConfirmSender.jsx';
import People from './pages/people/People.jsx';
import Person from './pages/people/Person.jsx';
import Suppliers from './pages/suppliers/Suppliers.jsx';
import Supplier from './pages/suppliers/Supplier.jsx';
import Planting from './pages/plantings/Planting.jsx';
import EventDetail from './pages/EventDetail.jsx';
import './theme.css';
import './styles.css';
import QuickStamp from './pages/stamp/QuickStamp.jsx';

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/approve-domain/:token" element={<ApproveDomain />} />
          <Route path="/confirm-sender/:token" element={<ConfirmSender />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="honeypot-domains" element={<AdminHoneypotDomains />} />
            <Route path="users/:userId" element={<AdminUser />} />
          </Route>
          <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
          <Route path="/setup" element={<RequireAuth><Setup /></RequireAuth>} />
          <Route path="/stamp/*" element={<RequireAuth><QuickStamp /></RequireAuth>} />
          <Route element={<RequireAuth><RequireOnboarded><Layout /></RequireOnboarded></RequireAuth>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/inbox/add" element={<InboxAdd />} />
            <Route path="/inbox/:messageId" element={<InboxMessage />} />
            <Route path="/inbox/:messageId/headers" element={<InboxHeaders />} />
            <Route path="/inbox/:messageId/view" element={<InboxView />} />
            <Route path="/outbox" element={<Outbox />} />
            <Route path="/company/lookalikes" element={<Lookalikes />} />
            <Route path="/company/lookalikes/:siteId" element={<LookalikeDetail />} />
            <Route path="/company/email-security" element={<EmailSecurity />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/help" element={<HelpIndex />} />
            <Route path="/help/:slug" element={<HelpArticle />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/outbox/:messageId" element={<OutboxMessage />} />
            <Route path="/outbox/:messageId/view" element={<InboxView box="outbox" />} />
            <Route path="/company" element={<Company />} />
            <Route path="/company/brands/:brandId" element={<Brand />} />
            <Route path="/company/domains/:domainId" element={<DomainVerify />} />
            <Route path="/company/website" element={<Website />} />
            <Route path="/company/team" element={<Team />} />
            <Route path="/company/email-services" element={<EmailServices />} />
            <Route path="/company/people" element={<People />} />
            <Route path="/company/people/:personId" element={<Person />} />
            <Route path="/company/suppliers" element={<Suppliers />} />
            <Route path="/company/suppliers/:supplierId" element={<Supplier />} />
            <Route path="/account" element={<Account />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:eventId" element={<EventDetail />} />
            <Route path="/events/:eventId/plantings/:plantingId" element={<Planting />} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
